import React, { useState } from "react";
import Draggable from "react-draggable";
import Portal from "../../common/components/Portal";
import "../validationModal.css";

// Otto's save-time validation modal on the recommendation form (FTO-134).
//
// WORDING. Every string here is deliberately impersonal: the narrative "shows", a field "displays".
// Nothing is addressed to the surveyor as "you", and the words "conflict", "disagree" and "mismatch" are
// avoided in our own copy. The point of the feature is to catch a fat-fingered keystroke, not to question
// anyone's assessment, and copy that reads as an accusation gets the feature resented rather than used.
// `evidence.reasoning` comes from Otto and is shown verbatim — its phrasing is a filter-ai prompt concern.

const STATE = {
    CHECKING: "checking",
    CLEAN: "clean",
    FINDINGS: "findings",
    FAILED: "failed"
};

const RULE_LABEL = {
    narrative_year_vs_install_year: "Narrative vs Install Year",
    narrative_condition_vs_recorded: "Narrative vs Condition"
};

const dash = v => (v === null || v === undefined || v === "" ? "—" : v);

// Years get tabular monospace so the two values line up digit for digit; a condition ("poor — \"condition is
// really bad\"") is prose and gets the app's own face, because mono sets prose much wider and wraps a short
// phrase onto three lines.
const isNumeric = v => v !== null && v !== undefined && v !== "" && /^\d+$/.test(String(v).trim());

// The backend labels each field with its band — "Installed Year (Additional Details)", "Condition
// (recommendation)". Only one band ever applies to a given recommendation (Asset vs System), so the
// qualifier adds length without telling the surveyor anything the form does not already show.
const fieldName = label => String(label || "").replace(/\s*\([^)]*\)\s*$/, "").trim();

const WarnIcon = () => (
    <svg viewBox="0 0 24 24" className="qav-ico" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
    </svg>
);

const TickIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M8.5 12.5l2.5 2.5 4.5-5" />
    </svg>
);

const IncompleteIcon = () => (
    <svg viewBox="0 0 24 24" className="qav-ico" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4.5" />
        <path d="M12 16h.01" />
    </svg>
);

// "Show more" appears only when the text is actually clipped — measured off the rendered element, so it
// never sits beside a sentence that already fits.
const Why = ({ text }) => {
    const [open, setOpen] = useState(false);
    const [clipped, setClipped] = useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const el = ref.current;
        if (el) setClipped(el.scrollHeight > el.clientHeight + 1);
    }, [text]);

    return (
        <div className="qav-why">
            <span className="qav-why-label">Why</span>
            <p className={`qav-why-text${open ? " is-open" : ""}`} ref={ref}>
                {text}
            </p>
            {clipped ? (
                <button type="button" className="qav-why-more non-draggable" onClick={() => setOpen(o => !o)}>
                    {open ? "Show less" : "Show more"}
                </button>
            ) : null}
        </div>
    );
};

// Driven entirely by the backend's labels, so a third rule renders here with no frontend change — hence
// nothing keys off `year_references`, which the condition rule does not have.
const Evidence = ({ evidence }) => {
    const comparison = evidence?.comparison;
    if (!comparison || !comparison.observed) return null;
    const expected = Array.isArray(comparison.expected) ? comparison.expected : [];

    // Only the field that differs: a recommendation routinely carries a second, blank year field.
    const primary = expected.find(f => f.conflicts) || expected[0];

    return (
        <>
            <div className="qav-faceoff">
                <div className="qav-cell">
                    <span className="qav-cell-cap">{fieldName(comparison.observed.label)} shows</span>
                    <span className={`qav-cell-val${isNumeric(comparison.observed.value) ? " is-num" : ""}`}>
                        {dash(comparison.observed.value)}
                    </span>
                </div>
                <span className="qav-vs">vs</span>
                <div className="qav-cell is-flagged">
                    <span className="qav-cell-cap">{primary ? `${fieldName(primary.label)} shows` : ""}</span>
                    <span className={`qav-cell-val${isNumeric(primary && primary.value) ? " is-num" : ""}`}>
                        {dash(primary && primary.value)}
                    </span>
                </div>
            </div>

            {evidence.reasoning ? <Why text={evidence.reasoning} /> : null}
        </>
    );
};

const ValidationModal = ({ state, findings = [], failed = [], checked = [], skipped = [], subject, onClose, onValidateAgain, onSaveAsIs }) => {
    // Inline rather than a second Portal: a nested modal over a draggable one fights the drag.
    const [confirmingClose, setConfirmingClose] = useState(false);
    const isChecking = state === STATE.CHECKING;
    const isClean = state === STATE.CLEAN;
    const isFindings = state === STATE.FINDINGS;
    const isFailed = state === STATE.FAILED;

    // A half-finished check that can be closed would leave the save in limbo.
    const closable = isFindings || isFailed;

    const count = findings.length;
    const heading = confirmingClose
        ? "Close without saving?"
        : isChecking
        ? "Validating recommendation"
        : isClean
          ? "Validation complete"
          : isFailed
            ? "Validation incomplete"
            : `${count} ${count === 1 ? "item needs" : "items need"} checking before saving this recommendation`;

    const tone = confirmingClose
        ? "is-incomplete"
        : isClean
          ? "is-clean"
          : isFailed
            ? "is-incomplete"
            : isFindings
              ? "is-findings"
              : "is-checking";

    const body = (
        <div className="modal modal-region modal-view qav-modal" id="modalId" tabIndex="-1" style={{ display: "block" }}>
            {/* Outside Draggable on purpose: react-draggable transforms what it wraps, and a
                viewport-sized box would drag offset from the cursor. */}
            <div className="qav-center">
                <Draggable handle=".draggable" cancel=".non-draggable">
                    <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className={`modal-header draggable qav-head ${tone}`}>
                            <span className="qav-head-ico">
                                {confirmingClose ? <IncompleteIcon /> : null}
                                {!confirmingClose && isChecking ? <span className="qav-spin" /> : null}
                                {!confirmingClose && isClean ? <TickIcon className="qav-ico" /> : null}
                                {!confirmingClose && isFindings ? <WarnIcon /> : null}
                                {!confirmingClose && isFailed ? <IncompleteIcon /> : null}
                            </span>
                            <div className="qav-head-text">
                                <h5 className="modal-title qav-title">{heading}</h5>
                                {subject && !confirmingClose ? <p className="qav-subject">{subject}</p> : null}
                            </div>
                            {closable ? (
                                <button type="button" className="close non-draggable" onClick={() => (confirmingClose ? setConfirmingClose(false) : setConfirmingClose(true))} aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            ) : null}
                        </div>

                        <div className="modal-body qav-body">
                            {confirmingClose ? (
                                <>
                                    <p className="qav-confirm-lead">
                                        You can <b>drag this panel aside</b> by its header and fix the fields with it
                                        still open, then use <b>Fixed, try again</b>.
                                    </p>
                                    <p className="qav-confirm-warn">
                                        Closing will <b>not</b> save this recommendation, and these Otto validation
                                        results are gone until you validate again.
                                    </p>
                                </>
                            ) : null}

                            {!confirmingClose && isChecking ? (
                                <>
                                    <p className="qav-lead">Otto is validating the following:</p>
                                    <ul className="qav-rules">
                                        <li>Narrative vs Install Year</li>
                                        <li>Narrative vs Condition</li>
                                    </ul>
                                    <div className="qav-bar">
                                        <i />
                                    </div>
                                </>
                            ) : null}

                            {!confirmingClose && isClean ? (
                                <div className="qav-clean">
                                    <TickIcon className="qav-clean-ico" />
                                    {/* "Looks good" is a claim about a COMPARISON. When every rule was skipped
                                        nothing was compared, and saying it anyway is how a narrative Otto could
                                        not read came to look like a narrative Otto had approved. */}
                                    <p className="qav-clean-text">{checked.length ? "Looks good" : "Nothing was compared"}</p>
                                    {checked.length ? (
                                        <ul className="qav-ran">
                                            {checked.map(key => (
                                                <li key={key}>{RULE_LABEL[key] || key}</li>
                                            ))}
                                        </ul>
                                    ) : null}
                                    {skipped.length ? (
                                        <div className="qav-skipped">
                                            <p className="qav-skipped-head">
                                                {checked.length ? "Not checked" : "Otto had nothing to compare"}
                                            </p>
                                            {/* The backend's own sentence, beside the rule it belongs to — it
                                                states the fact and stops, so it reads correctly whether the
                                                surveyor fixes the narrative or saves as is. */}
                                            {skipped.map((s, i) => (
                                                <p className="qav-skipped-msg" key={s.rule_key || i}>
                                                    <b>{s.short_title || RULE_LABEL[s.rule_key] || s.rule_key}</b> — {s.message}
                                                </p>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            {!confirmingClose && isFindings
                                ? findings.map((finding, i) => (
                                      <div className="qav-finding" key={finding.rule_key || i}>
                                          <p className="qav-rule">
                                              {count > 1 ? <span className="qav-n">{i + 1}</span> : null}
                                              {finding.short_title || RULE_LABEL[finding.rule_key] || finding.title}
                                          </p>
                                          <Evidence evidence={finding.evidence} />
                                      </div>
                                  ))
                                : null}

                            {!confirmingClose && isFailed ? (
                                <>
                                    {/* The backend's own sentence. It states the fact and stops — no remedy — so the
                                        same message reads correctly whether the surveyor retries or saves as is. */}
                                    {failed.map((f, i) => (
                                        <p className="qav-failed-msg" key={f.rule_key || i}>
                                            {f.message}
                                        </p>
                                    ))}
                                    <p className="qav-failed-sub">Recommendation validation is not completed.</p>
                                </>
                            ) : null}
                        </div>

                        {!confirmingClose && isChecking ? (
                            <div className="modal-footer qav-foot">
                                <button type="button" className="btn btn-primary btnRgion" disabled>
                                    Validating…
                                </button>
                            </div>
                        ) : null}

                        {confirmingClose ? (
                            <div className="modal-footer qav-foot">
                                <button type="button" className="btn btn-secondary btnClr non-draggable" onClick={onClose}>
                                    Close anyway
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btnRgion non-draggable"
                                    onClick={() => setConfirmingClose(false)}
                                >
                                    Keep it open
                                </button>
                            </div>
                        ) : null}

                        {(isFindings || isFailed) && !confirmingClose ? (
                            <div className="modal-footer qav-foot">
                                {/* Close does NOT save; it only dismisses. Kept apart from "Save as is",
                                    which does. */}
                                <button type="button" className="btn btn-secondary btnClr non-draggable qav-foot-close" onClick={() => setConfirmingClose(true)}>
                                    Close
                                </button>
                                <button type="button" className="btn btn-secondary btnClr non-draggable" onClick={onSaveAsIs}>
                                    Save recommendation as is
                                </button>
                                <button type="button" className="btn btn-primary btnRgion non-draggable" onClick={onValidateAgain}>
                                    {isFailed ? "Try again" : "Fixed, try again"}
                                </button>
                            </div>
                        ) : null}
                    </div>
                    </div>
                </Draggable>
            </div>
        </div>
    );

    // noOverlay drops the Portal's pointer-event trap so the form behind stays editable while this is
    // dragged aside. Kept on while Otto works — there is nothing to go and fix yet.
    return <Portal body={body} noOverlay={closable} onCancel={closable ? onClose : () => {}} />;
};

ValidationModal.STATE = STATE;

export default ValidationModal;
